import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  useTheme,
  LinearProgress,
  Fade,
} from '@mui/material';
import { IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import AuthService from '../services/authService';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const authService = AuthService.getInstance();
  const theme = useTheme();
  const themeMode = theme.palette.mode;

  // Tighten vertical rhythm on short desktop viewports (720/768/864/900px screens minus browser chrome)
  const shortViewport = '@media (max-height: 800px)';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setLoadingMessage('Authenticating with Shipium...');

    try {
      setLoadingMessage('Validating credentials...');
      const loginResponse = await authService.login(username, password);
      
      setLoadingMessage('Checking user permissions...');
      
      // Debug: Log the user data and authorities
      console.log('Login response:', loginResponse);
      console.log('User authorities:', loginResponse.user?.securityInfo?.authorities);
      console.log('Is Site Admin:', authService.isSiteAdmin());
      
      // Check if user has SITE_ADMIN role
      if (!authService.isSiteAdmin()) {
        setError(`Access denied. SITE_ADMIN role required. Your roles: ${loginResponse.user?.securityInfo?.authorities?.join(', ') || 'None'}`);
        authService.logout();
        return;
      }

      // Force immediate auth state update and redirect
      setLoadingMessage('Redirecting to dashboard...');
      // Force a small delay to show the redirect message, then reload to ensure proper state
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 200);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: { xs: 400, xl: 440 } }}>
          {/* Shipium Logo (with fallback) */}
          <Box sx={{ p: 3, pb: 1, display: 'flex', justifyContent: 'center', [shortViewport]: { p: 2, pb: 0 } }}>
            {logoError ? (
              <Typography variant="h6" color="text.secondary" align="center">
                Shipium
              </Typography>
            ) : (
              <Box
                component="img"
                src={"/Shipium-Logo-640x360.png"}
                alt="Shipium Logo"
                sx={{
                  // Size by viewport height so the 16:9 logo never pushes the form below the fold
                  height: 'clamp(72px, 18vh, 200px)',
                  [shortViewport]: { height: 'clamp(64px, 14vh, 112px)' },
                  width: 'auto',
                  maxWidth: '100%',
                  // Apply filter to make black text white in dark mode while preserving the green logo
                  filter: themeMode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                }}
                onError={() => setLogoError(true)}
              />
            )}
          </Box>

          <CardHeader
            sx={{ [shortViewport]: { py: 1 } }}
            title={
              <Typography component="h1" variant="h5" align="center">
                Shipium Partner Assistant
              </Typography>
            }
            subheader={
              <Typography variant="body2" align="center" color="text.secondary">
                Export and monitor partner data from Shipium
              </Typography>
            }
          />
          <CardContent sx={{ pt: 0, [shortViewport]: { pb: '12px !important' } }}>
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Fade in={!!error}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                </Fade>
              )}
              
              {loading && (
                <Fade in={loading}>
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                      {loadingMessage}
                    </Typography>
                  </Box>
                </Fade>
              )}
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Email Address"
                name="username"
                autoComplete="email"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiInputBase-root': {
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiInputBase-root': {
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  minHeight: 48,
                  [shortViewport]: { mt: 2, mb: 1.5 },
                  transition: 'all 0.2s ease-in-out',
                  '&:disabled': {
                    opacity: 0.7,
                  },
                }}
                disabled={loading || !username || !password}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <Typography variant="body2">Signing In...</Typography>
                  </Box>
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <Typography variant="body2" color="text.secondary" align="center">
                Only Shipium administrators can access this application.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      
      {/* Copyright Footer */}
      <Box 
        component="footer" 
        sx={{
          py: 2,
          px: 3,
          textAlign: 'center',
          [shortViewport]: { py: 1 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Copyright Shipium, Inc. © {new Date().getFullYear()}
        </Typography>
      </Box>
    </Container>
  );
};

export default LoginPage;
